import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Search, Download, Loader2, ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";
import { format } from "date-fns";

interface MailingRecord {
  id: string;
  seller_id: string;
  county: string | null;
  origin_state: string | null;
  recorded_date: string | null;
  pid: string | null;
  first_name: string | null;
  last_name: string | null;
  suffix: string | null;
  business_name: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  consideration: number | null;
  document_type: string | null;
  property_address: string | null;
  response: string | null;
  phone_number: string | null;
  email_address: string | null;
  household_income: string | null;
  tm_notes: string | null;
  tm_dropdown: string | null;
  mailing_status: string;
  times_mailed: number;
  last_mailed_date: string | null;
}

type SortField = keyof MailingRecord;

const ALL_COLUMNS: { key: SortField; label: string; width?: string }[] = [
  { key: "seller_id", label: "Seller ID", width: "100px" },
  { key: "county", label: "County", width: "100px" },
  { key: "origin_state", label: "Origin St", width: "60px" },
  { key: "recorded_date", label: "Recorded", width: "90px" },
  { key: "pid", label: "PID", width: "90px" },
  { key: "first_name", label: "First", width: "90px" },
  { key: "last_name", label: "Last", width: "100px" },
  { key: "suffix", label: "Sfx", width: "40px" },
  { key: "business_name", label: "Business", width: "140px" },
  { key: "street_address", label: "Street", width: "160px" },
  { key: "city", label: "City", width: "100px" },
  { key: "state", label: "St", width: "40px" },
  { key: "zip_code", label: "Zip", width: "60px" },
  { key: "consideration", label: "Consid.", width: "80px" },
  { key: "document_type", label: "Doc Type", width: "90px" },
  { key: "property_address", label: "Property Addr", width: "180px" },
  { key: "response", label: "Response", width: "100px" },
  { key: "phone_number", label: "Phone", width: "100px" },
  { key: "email_address", label: "Email", width: "140px" },
  { key: "household_income", label: "Income", width: "80px" },
  { key: "tm_notes", label: "TM Notes", width: "140px" },
  { key: "tm_dropdown", label: "TM Drop", width: "80px" },
  { key: "mailing_status", label: "Status", width: "70px" },
  { key: "times_mailed", label: "#Mail", width: "50px" },
  { key: "last_mailed_date", label: "Last Mailed", width: "90px" },
];

export function DatasheetView() {
  const [records, setRecords] = useState<MailingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("last_name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 200;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, sortField, sortDir]);

  useEffect(() => {
    fetchRecords();
  }, [page, debouncedSearch, sortField, sortDir]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("mailing_records")
        .select("*", { count: "exact" });

      if (debouncedSearch) {
        query = query.or(
          `last_name.ilike.%${debouncedSearch}%,first_name.ilike.%${debouncedSearch}%,business_name.ilike.%${debouncedSearch}%,seller_id.ilike.%${debouncedSearch}%,county.ilike.%${debouncedSearch}%,street_address.ilike.%${debouncedSearch}%,city.ilike.%${debouncedSearch}%,property_address.ilike.%${debouncedSearch}%,response.ilike.%${debouncedSearch}%`
        );
      }

      query = query.order(sortField, { ascending: sortDir === "asc", nullsFirst: false });
      query = query.range(page * pageSize, (page + 1) * pageSize - 1);

      const { data, count, error } = await query;
      if (error) throw error;
      setRecords((data || []) as MailingRecord[]);
      setTotalCount(count || 0);
    } catch (e: any) {
      toast.error("Failed to load: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const exportAll = async () => {
    toast.info("Exporting all records...");
    let allData: any[] = [];
    let from = 0;
    const chunk = 1000;
    while (true) {
      let q = supabase.from("mailing_records").select("*").order(sortField, { ascending: sortDir === "asc" });
      if (debouncedSearch) {
        q = q.or(`last_name.ilike.%${debouncedSearch}%,first_name.ilike.%${debouncedSearch}%,business_name.ilike.%${debouncedSearch}%,seller_id.ilike.%${debouncedSearch}%,county.ilike.%${debouncedSearch}%`);
      }
      const { data } = await q.range(from, from + chunk - 1);
      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      if (data.length < chunk) break;
      from += chunk;
    }
    const rows = allData.map(r => {
      const obj: Record<string, any> = {};
      ALL_COLUMNS.forEach(c => { obj[c.label] = r[c.key] ?? ""; });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "All Records");
    XLSX.writeFile(wb, `mailing-datasheet-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success(`Exported ${allData.length} records`);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-0.5 opacity-30 inline" />;
    return sortDir === "asc"
      ? <ArrowUp className="h-3 w-3 ml-0.5 inline" />
      : <ArrowDown className="h-3 w-3 ml-0.5 inline" />;
  };

  const formatCell = (record: MailingRecord, key: SortField) => {
    const val = record[key];
    if (val === null || val === undefined || val === "") return "";
    if (key === "consideration") return typeof val === "number" ? `$${val.toLocaleString()}` : val;
    if (key === "times_mailed") return String(val);
    return String(val);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search all fields..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {totalCount.toLocaleString()} records
          </Badge>
          <Button variant="outline" size="sm" onClick={exportAll}>
            <Download className="h-4 w-4 mr-1" /> Export All
          </Button>
        </div>
      </div>

      {/* Datasheet */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-md border overflow-auto" style={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                {ALL_COLUMNS.map(col => (
                  <TableHead
                    key={col.key}
                    className="cursor-pointer whitespace-nowrap px-2 py-1.5 text-xs font-semibold select-none"
                    style={{ minWidth: col.width }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    <SortIcon field={col.key} />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={ALL_COLUMNS.length} className="text-center py-8 text-muted-foreground">
                    No records found
                  </TableCell>
                </TableRow>
              ) : records.map(record => (
                <TableRow key={record.id} className="hover:bg-muted/30">
                  {ALL_COLUMNS.map(col => (
                    <TableCell
                      key={col.key}
                      className="px-2 py-1 text-xs whitespace-nowrap max-w-[200px] truncate"
                      title={String(record[col.key] ?? "")}
                    >
                      {col.key === "response" && record.response ? (
                        <Badge variant="outline" className="text-xs px-1 py-0">{record.response}</Badge>
                      ) : col.key === "mailing_status" ? (
                        <Badge variant={record.mailing_status === "active" ? "default" : "secondary"} className="text-xs px-1 py-0">
                          {record.mailing_status}
                        </Badge>
                      ) : (
                        formatCell(record, col.key)
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page + 1} of {totalPages} ({totalCount.toLocaleString()} records)
          </span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(0)}>
              First
            </Button>
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
